import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { FilmIcon, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative z-10 min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1759256243437-9c8f7238c42b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBleHRlcmlvciUyMHJlYWwlMjBlc3RhdGUlMjBwb29sJTIwZXZlbmluZ3xlbnwwfHx8fDE3ODQxODcxOTZ8MA&ixlib=rb-4.1.0&q=85"
          alt="Luxury property"
          className="co-kenburns absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-black/95 via-black/40 to-transparent" />
        <Link to="/" className="absolute left-12 top-12 flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center">
            <FilmIcon className="w-5 h-5 text-black" strokeWidth={2} />
          </div>
          <div className="text-xl font-heading font-bold tracking-tight">MediaOthers</div>
        </Link>
        <div className="absolute left-12 bottom-14 max-w-md">
          <div className="font-mono-tech text-[10px] text-amber-400 mb-4">AI · MEDIA · STUDIO</div>
          <h1 className="font-heading text-5xl xl:text-6xl leading-none tracking-tighter font-light">
            Real AI <span className="font-bold text-amber-400">cinematic</span> video<br />
            from a single <span className="font-bold">photo</span>.
          </h1>
          <p className="mt-6 text-zinc-300 text-base max-w-sm">
            Kling 3, Veo 3, Runway, Luma and Hailuo — one platform, every top model.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12 relative">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div>
            <div className="font-mono-tech text-[10px] text-amber-400 mb-3">STUDIO · ACCESS</div>
            <h2 className="font-heading text-3xl font-bold tracking-tight">Sign in</h2>
            <p className="text-zinc-400 text-sm mt-2">Welcome back to MediaOthers.</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="font-mono-tech text-[10px] text-zinc-500">EMAIL</Label>
            <Input id="email" data-testid="login-email" type="email" required
              value={email} onChange={(e) => setEmail(e.target.value)}
              className="bg-[#0b0b0d] border-white/10 focus-visible:border-amber-500 focus-visible:ring-amber-500 h-11" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="font-mono-tech text-[10px] text-zinc-500">PASSWORD</Label>
            <Input id="password" data-testid="login-password" type="password" required
              value={password} onChange={(e) => setPassword(e.target.value)}
              className="bg-[#0b0b0d] border-white/10 focus-visible:border-amber-500 focus-visible:ring-amber-500 h-11" />
          </div>

          <Button type="submit" disabled={busy} data-testid="login-submit"
            className="w-full h-11 bg-amber-500 hover:bg-amber-600 text-black font-semibold tracking-tight">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {busy ? "Signing in…" : "Enter Studio"}
          </Button>

          <div className="text-center text-sm text-zinc-400 pt-2">
            Don&apos;t have an account?{" "}
            <Link to="/signup" data-testid="link-signup" className="text-amber-400 hover:text-amber-300 font-medium">
              Sign up
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
